import React, { InputHTMLAttributes, useLayoutEffect, useRef, useState } from 'react';
import cn from 'classnames';

import s from './index.module.css';

interface IInputBaseProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'placeholder'> {
  startElement?: React.ReactNode;
  endElement?: React.ReactNode;
  ghostText?: string;
  defaultValue?: never;
  hasFocusHighlight?: boolean;
  hasError?: boolean;
  dataTestId?: string;
  placeholder: React.ReactNode;
  isMultiline?: boolean;
}

const getWidth = (nodeRef: React.RefObject<HTMLElement>): number => {
  if (nodeRef?.current) {
    return nodeRef.current.clientWidth;
  }

  return 0;
};

const isEmpty = (value: string | number | readonly string[] | undefined) =>
  value === '' || value === null || value === undefined;

const InputBase = React.forwardRef(
  (props: IInputBaseProps, ref: React.ForwardedRef<HTMLInputElement>) => {
    const {
      startElement,
      endElement,
      ghostText,
      value,
      hasFocusHighlight,
      className,
      hasError,
      disabled,
      type,
      placeholder,
      dataTestId = 'input',
      ...inputProps
    } = props;

    const startRef = useRef<HTMLDivElement>(null);
    const endRef = useRef<HTMLDivElement>(null);
    const ghostTextRef = useRef<HTMLDivElement>(null);
    const [paddingStart, setPaddingStart] = useState(10);
    const [paddingEnd, setPaddingEnd] = useState(10);

    useLayoutEffect(() => {
      const startWidth = getWidth(startRef);

      if (startWidth && paddingStart !== startWidth) {
        setPaddingStart(startWidth);
      }

      const endWidth = getWidth(endRef);

      if (endWidth && paddingEnd !== endWidth) {
        setPaddingEnd(endWidth);
      }
    }, [paddingStart, paddingEnd, ghostText, ghostTextRef, startRef, endRef]);

    return (
      <div
        data-test-id={`${dataTestId}__wrapper`}
        className={s.wrapper}
        style={
          {
            '--input-start-padding': paddingStart + 'px',
            '--input-end-padding': paddingEnd + 'px',
          } as any
        }
      >
        <input
          {...inputProps}
          type={type}
          disabled={disabled}
          ref={ref}
          value={value}
          className={cn(className, s.input, {
            [s.hasFocus]: hasFocusHighlight,
            [s.isDisabled]: disabled,
            [s.danger]: hasError,
          })}
          data-test-id={dataTestId}
        />
        {ghostText && (
          <div className={s.ghostText} ref={ghostTextRef}>
            <div className={s.ghostTextVisibleBox} data-test-id={`${dataTestId}__ghost-text`}>
              <span className={s.ghostIndent}>{value}</span>
              {ghostText}
            </div>
          </div>
        )}
        {isEmpty(value) && (
          <div data-test-id={`${dataTestId}__placeholder`} className={s.placeholder}>
            {placeholder}
          </div>
        )}
        {startElement && (
          <div className={s.start} ref={startRef} data-test-id={`${dataTestId}__before`}>
            {startElement}
          </div>
        )}
        {endElement && (
          <div className={s.end} ref={endRef} data-test-id={`${dataTestId}__after`}>
            {endElement}
          </div>
        )}
      </div>
    );
  },
);

export type { IInputBaseProps };
export { InputBase };
